import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { resolveInitialMapView } from '../utils/initialMapView';
import { getUserTimezone } from '../utils/timezone';
import { fetchServerMapView, writeLocalMapView, createDebouncedMapViewSaver } from '../utils/mapViewStorage';

/**
 * LiveTrackingContext — persists Live Tracking page state across navigation AND page refresh.
 * Lives above the router so it never unmounts when switching pages.
 * Key state is backed up to sessionStorage so it survives F5 refresh.
 */
const LiveTrackingContext = createContext(null);

export const useLiveTracking = () => {
  const ctx = useContext(LiveTrackingContext);
  if (!ctx) throw new Error('useLiveTracking must be used within LiveTrackingProvider');
  return ctx;
};

const STORAGE_KEY = 'lt_state';

// Load persisted state from sessionStorage
const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
};

// Detect a sensible starting timezone even before user settings load.
const detectTimezone = () => {
  try {
    return getUserTimezone() || Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch { return null; }
};

export const LiveTrackingProvider = ({ children }) => {
  // One-time snapshot of persisted state, used only to seed the initial useState
  // values below. Held in state (lazy-init) rather than a ref so the seed reads
  // (`s?.field`) are not ref-current reads during render (react-hooks/refs) — and
  // so sessionStorage is parsed once on mount instead of on every provider render.
  const [s] = useState(loadPersistedState);

  // Resolve the initial map view once, synchronously.
  // Priority: sessionStorage → local persistent cache → user's timezone → Sydney.
  const [initialView] = useState(() => resolveInitialMapView(
    'live_tracking',
    s?.lastMapCenter ? { center: s.lastMapCenter, zoom: s.lastMapZoom } : null,
    detectTimezone(),
  ));

  // ── Device selection & visibility ──
  const [selectedDevice, setSelectedDevice] = useState(s?.selectedDevice || null);
  const [followedDevices, setFollowedDevices] = useState(() => new Set(s?.followedDevices || []));
  const [hiddenDevices, setHiddenDevices] = useState(() => new Set(s?.hiddenDevices || []));
  const [trailEnabledDevices, setTrailEnabledDevices] = useState(() => new Set(s?.trailEnabledDevices || []));

  // ── Map state ──
  const [lastMapCenter, setLastMapCenter] = useState(initialView.center);
  const [lastMapZoom, setLastMapZoom] = useState(initialView.zoom);
  const [mapType, setMapType] = useState(s?.mapType || null);
  const [showTraffic, setShowTraffic] = useState(s?.showTraffic || false);
  const [showPOI, setShowPOI] = useState(s?.showPOI || false);
  const [showHeatmap, setShowHeatmap] = useState(s?.showHeatmap || false);
  const [darkModeMap, setDarkModeMap] = useState(s?.darkModeMap || false);
  const [showWeather, setShowWeather] = useState(s?.showWeather || false);
  const [weatherLayer, setWeatherLayer] = useState(s?.weatherLayer || 'precipitation_new');
  const [showGeofenceOverlay, setShowGeofenceOverlay] = useState(s?.showGeofenceOverlay || false);
  const [geofenceOverlayTypes, setGeofenceOverlayTypes] = useState(s?.geofenceOverlayTypes || {
    zones: true,
    places: true,
    routes: true,
  });

  // ── Feature settings (loaded from API) ──
  const [mapFeatureSettings, setMapFeatureSettings] = useState(s?.mapFeatureSettings || null);

  // ── Road Speed Limits (persist across navigation) ──
  const [roadSpeedLimits, setRoadSpeedLimits] = useState(s?.roadSpeedLimits || {});

  // ── Device List sidebar state (DeviceListPanel child component) ──
  // Each DeviceListPanel unmount destroys its local state, so these live here.
  const [deviceListSearchQuery, setDeviceListSearchQuery] = useState(s?.deviceListSearchQuery || '');
  const [deviceListStatusFilter, setDeviceListStatusFilter] = useState(s?.deviceListStatusFilter || 'all');
  const [deviceListIsGrouped, setDeviceListIsGrouped] = useState(s?.deviceListIsGrouped !== undefined ? s.deviceListIsGrouped : true);
  const [deviceListCollapsedGroups, setDeviceListCollapsedGroups] = useState(() => new Set(s?.deviceListCollapsedGroups || []));

  // ── Refs (survive navigation but not page refresh) ──
  const positionHistoryRef = useRef({});
  const stopTimestampsRef = useRef({});
  const animStateRef = useRef({});
  const animPositionsRef = useRef({});

  // ── Async server reconcile: pull the last-saved view from the server on cold-mount
  // and warm the local cache for the NEXT cold load. Never overwrites the CURRENT
  // in-session view (which the user may already have panned/zoomed).
  useEffect(() => {
    // Only reconcile on true cold-load (no sessionStorage view AND we didn't already use a local cache)
    if (initialView.source !== 'timezone' && initialView.source !== 'fallback') return;
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    const token = sessionStorage.getItem('impersonate_token') || sessionStorage.getItem('quick_login_token') || localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!apiBase || !token) return;
    let cancelled = false;
    (async () => {
      const server = await fetchServerMapView('live_tracking', apiBase, token);
      if (cancelled || !server) return;
      // Warm the local cache; the current in-memory view stays where it is to
      // avoid a visible jump. Next cold-load will read the local cache.
      writeLocalMapView('live_tracking', server);
    })();
    return () => { cancelled = true; };
    // Run exactly once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Debounced save of map view (local + server) when the user pans/zooms ──
  // Skip the first render (which just re-emits the seeded initial view) so we don't
  // fire wasteful PATCHes on every cold mount / nav-back. Only real user movement saves.
  const saveMapViewRef = useRef(null);
  const isInitialSaveMountRef = useRef(true);
  useEffect(() => {
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    if (!apiBase) return;
    saveMapViewRef.current = createDebouncedMapViewSaver(
      'live_tracking',
      apiBase,
      () => sessionStorage.getItem('impersonate_token') || sessionStorage.getItem('quick_login_token') || localStorage.getItem('token') || sessionStorage.getItem('token'),
    );
  }, []);
  useEffect(() => {
    if (isInitialSaveMountRef.current) { isInitialSaveMountRef.current = false; return; }
    if (!saveMapViewRef.current || !lastMapCenter) return;
    saveMapViewRef.current({ lat: lastMapCenter.lat, lng: lastMapCenter.lng, zoom: lastMapZoom });
  }, [lastMapCenter, lastMapZoom]);

  // ── Save to sessionStorage on state changes ──
  useEffect(() => {
    const state = {
      selectedDevice,
      followedDevices: [...followedDevices],
      hiddenDevices: [...hiddenDevices],
      trailEnabledDevices: [...trailEnabledDevices],
      lastMapCenter, lastMapZoom, mapType,
      showTraffic, showPOI, showHeatmap, darkModeMap, showWeather, weatherLayer, showGeofenceOverlay, geofenceOverlayTypes,
      mapFeatureSettings, roadSpeedLimits,
      deviceListSearchQuery, deviceListStatusFilter, deviceListIsGrouped,
      deviceListCollapsedGroups: [...deviceListCollapsedGroups],
    };
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [selectedDevice, followedDevices, hiddenDevices, trailEnabledDevices,
      lastMapCenter, lastMapZoom, mapType, showTraffic, showPOI, showHeatmap,
      darkModeMap, showWeather, weatherLayer, showGeofenceOverlay, geofenceOverlayTypes, mapFeatureSettings, roadSpeedLimits,
      deviceListSearchQuery, deviceListStatusFilter, deviceListIsGrouped, deviceListCollapsedGroups]);

  // ── Toggle helpers ──
  const toggleFollow = useCallback((deviceId) => {
    setFollowedDevices(prev => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  }, []);

  const toggleHide = useCallback((deviceId) => {
    setHiddenDevices(prev => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  }, []);

  const toggleDeviceTrail = useCallback((deviceId) => {
    setTrailEnabledDevices(prev => {
      const next = new Set(prev);
      if (next.has(deviceId)) {
        next.delete(deviceId);
        // Clear trail history when hiding
        delete positionHistoryRef.current[deviceId];
      } else {
        // Clear any stale history when showing fresh
        delete positionHistoryRef.current[deviceId];
        next.add(deviceId);
      }
      return next;
    });
  }, []);

  return (
    <LiveTrackingContext.Provider value={{
      selectedDevice, setSelectedDevice,
      followedDevices, setFollowedDevices, toggleFollow,
      hiddenDevices, setHiddenDevices, toggleHide,
      trailEnabledDevices, setTrailEnabledDevices, toggleDeviceTrail,
      lastMapCenter, setLastMapCenter,
      lastMapZoom, setLastMapZoom,
      mapType, setMapType,
      showTraffic, setShowTraffic,
      showPOI, setShowPOI,
      showHeatmap, setShowHeatmap,
      darkModeMap, setDarkModeMap,
      showWeather, setShowWeather,
      weatherLayer, setWeatherLayer,
      showGeofenceOverlay, setShowGeofenceOverlay,
      geofenceOverlayTypes, setGeofenceOverlayTypes,
      mapFeatureSettings, setMapFeatureSettings,
      roadSpeedLimits, setRoadSpeedLimits,
      deviceListSearchQuery, setDeviceListSearchQuery,
      deviceListStatusFilter, setDeviceListStatusFilter,
      deviceListIsGrouped, setDeviceListIsGrouped,
      deviceListCollapsedGroups, setDeviceListCollapsedGroups,
      positionHistoryRef, stopTimestampsRef,
      animStateRef, animPositionsRef,
    }}>
      {children}
    </LiveTrackingContext.Provider>
  );
};
